<script lang="ts" setup>
  import { computed, onMounted, ref, toRefs } from 'vue'
  import { DateTime } from 'luxon'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'

  import { useGeneratedEmployeeStore, useGeneratedWorktimeTrackingDayVersionStore } from '@/stores'
  import type { EmployeeJsonldDefaultRead, WorktimeTrackingDayVersionJsonldDefaultRead } from '@/api'
  import { AutoSetIcon, InternalLinkIcon, NoteIcon } from '@/config/icons'

  import PageLoading from '@/components/PageLoading.vue'

  import DataTable from 'primevue/datatable'
  import Dialog from 'primevue/dialog'
  import TabPanel from 'primevue/tabpanel'
  import Tag from 'primevue/tag'
  import { useMediaQuery } from '@vueuse/core'

  export interface WorktimeVersionsOverviewProps {
    versionsArray: string[]
    date: string
    activeVersion?: string
    relatedEmployee: string
  }

  const props = defineProps<WorktimeVersionsOverviewProps>()
  const { versionsArray, date, relatedEmployee, activeVersion } = toRefs(props)

  const emit = defineEmits(['linkOpen'])

  const wttVersionStore = useGeneratedWorktimeTrackingDayVersionStore()
  const empStore = useGeneratedEmployeeStore()
  const { t } = useI18n()
  const router = useRouter()
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const isVisible = defineModel<boolean>('isVisible', { default: false })
  const isLoading = ref<boolean>(true)
  const activeTabIndex = ref(activeVersion.value ? versionsArray.value.indexOf(activeVersion.value) : 0)

  const relatedVersions = ref<WorktimeTrackingDayVersionJsonldDefaultRead[]>([])
  const allEmployees = ref<Record<string, EmployeeJsonldDefaultRead>>({})

  onMounted(async () => {
    relatedVersions.value = (
      await wttVersionStore.findAll(
        {
          query: {
            'id[]': versionsArray.value
              .map((v) => {
                const match = /\/(\d+)$/.exec(v)
                return match ? parseInt(match[1], 10) : -1
              })
              .filter((num) => num > -1)
          }
        },
        true
      )
    ).value
    allEmployees.value = (await empStore.findAllAsIriDictionary({})).value
    isLoading.value = false
  })

  const getTitle = (index: number) => {
    return 'Version ' + (index + 1)
  }

  const getHeader = computed(() => {
    return `Versionsübersicht. Datum: ${DateTime.fromISO(date.value).toFormat('dd.LL.yyyy')}. Mitarbeiter: ${allEmployees.value[relatedEmployee.value].displayname}`
  })

  const openLink = (id: string) => {
    emit('linkOpen')
    void router.push({
      name: 'working-time-corr-requests',
      query: { dayID: id }
    })
  }

  const isSystemMessage = (note: string) => {
    return note === 'Wurde automatisch generiert. Grund: Statusänderung.'
  }

  const translation = {
    "BREAK_BEGIN": "Pause begonnen",
    "BREAK_END": "Pause beendet",
    "WORK_BEGIN": "Arbeit begonnen",
    "WORK_END": "Arbeit beendet",
    "Pause begonnen": "BREAK_BEGIN",
    "Pause beendet": "BREAK_END",
    "Arbeit begonnen": "WORK_BEGIN",
    "Arbeit beendet": "WORK_END"
  }
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :header="!isLoading ? getHeader : ''"
    class="max-w-full md:max-w-screen-lg"
    modal>
    <template v-if="isLoading">
      <PageLoading
        v-show="isLoading"
        class="mx-4" />
    </template>
    <div v-else>
      <Tabs :value="0">
        <TabList>
          <Tab
            v-for="(vers, index) in relatedVersions"
            :key="vers.id"
            :value="index"
            class="text-sm">
            Version {{ index + 1 }}
            <Tag
              v-if="vers['@id'] == activeVersion"
              severity="success"
              value="Aktiv" />
            <Tag
              v-if="vers.decisionPending"
              severity="info"
              value="Neu" />
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel
            v-for="(vers, index) in relatedVersions"
            :key="vers.id"
            :value="index">
            <div class="flex justify-between text-sm">
              <span>Ersteller: {{ allEmployees[vers.creator!].displayname }}</span>
              <span>Erstellt am: {{ $formatter.DateTime(vers.createdAt) }}</span>
            </div>
            <div
              v-if="$titrop.user.roles.management && vers.decisionPending"
              class="mt-2 hover:underline flex cursor-pointer"
              @click="openLink(vers.day!)">
              <span class="align-text-bottom">in ausstehende Korrekturanfragen öffnen</span>
              <InternalLinkIcon />
            </div>
            <Divider class="mt-3" />
            <DataTable
              :value="vers.entries"
              size="small"
              class="text-sm">
              <Column
                :field="(v) => $formatter.TimeOnly(v.timestamp)"
                header="Zeitpunkt" />
              <Column
                :frozen="false"
                field="action"
                header="Aktion">
                <template #body="data">
                  <div class="flex">
                    {{ translation[data.data.action] }}
                    <span
                      v-if="data.data.note"
                      class="flex">
                      <span
                        v-tooltip="{
                          value: data.data.note,
                          showOnDisabled: true,
                          event: 'focus',
                          hideDelay: isDesktop ? 0 : 2000,
                          modifiers: { top: true }
                        }"
                        class="ml-2">
                        <AutoSetIcon v-if="isSystemMessage(data.data.note)" />
                        <NoteIcon v-else />
                      </span>
                    </span>
                    <span
                      v-if="data.data.draft"
                      v-tooltip="{
                        value: 'Wurde automatisch generiert. Grund: fehlender Eintrag.',
                        showOnDisabled: true,
                        event: 'focus',
                        hideDelay: isDesktop ? 0 : 2000,
                        modifiers: { top: true }
                      }"
                      class="ml-2 flex">
                      <AutoSetIcon />
                    </span>
                  </div>
                </template>
              </Column>
            </DataTable>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </Dialog>
</template>

<i18n>
{
  "de": {
    "BREAK_BEGIN": "Pause begonnen",
    "BREAK_END": "Pause beendet",
    "WORK_BEGIN": "Arbeit begonnen",
    "WORK_END": "Arbeit beendet",
    "Pause begonnen": "BREAK_BEGIN",
    "Pause beendet": "BREAK_END",
    "Arbeit begonnen": "WORK_BEGIN",
    "Arbeit beendet": "WORK_END"
  }
}
</i18n>

<style scoped>
  :deep(.p-tabview-header.p-highlight) {
    background-color: #ffffff;
    color: #000000;
    border-bottom: 3px solid #6366f1;
    margin-bottom: 4px;
  }

  :deep(.p-tabview-nav-link) {
    background-color: #ffffff;
    border: unset;
  }

  :deep(.p-tab) {
    padding: 0.325rem 0.5rem;
  }
</style>
